'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useMediaSource, type MediaVideoFull } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';

/**
 * Portrait short-form video rail — fed by
 * {@code MediaSource.videos.fetchShorts}.
 */
export interface ShortsRailProps {
  title?: string;
  limit?: number;
  shortHrefTemplate?: string;
}

function fmtDuration(seconds: number | undefined): string | undefined {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return undefined;
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export function ShortsRail({
  title,
  limit = 10,
  shortHrefTemplate = '/tube/{slug}',
}: ShortsRailProps) {
  const { t } = useTranslation('site-builder-blocks');
  const heading = title ?? t('shortsRail.title');
  const media = useMediaSource();

  const state = useAsyncResource<MediaVideoFull[]>(
    async (signal) => {
      if (!media) return null;
      const items = await media.videos.fetchShorts({ limit, signal });
      return items.length > 0 ? items : null;
    },
    [media, limit],
  );

  if (!media) {
    return (
      <SectionShell title={heading}>
        <AdapterPlaceholder blockType="ShortsRail" adapterName="MediaSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={heading}>
        <SkeletonRow count={limit} layout="rail" aspect="9 / 16" minCardWidth={160} />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={heading}>
        <EmptyState title={t('shortsRail.empty.title')} hint={t('shortsRail.empty.hint')} />
      </SectionShell>
    );
  }

  return (
    <SectionShell title={heading}>
      <ContentRail ariaLabel={heading} itemWidth="180px" snap="start">
        {state.data.map((short) => (
          <Link
            key={short.id}
            href={shortHrefTemplate.replace('{slug}', encodeURIComponent(short.slug))}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              aria-hidden
              style={{
                aspectRatio: '9 / 16',
                borderRadius: 'var(--radius-md, 8px)',
                background: short.posterUrl
                  ? `url(${short.posterUrl}) center/cover no-repeat`
                  : 'var(--color-neutral-200, #e4e4e7)',
                position: 'relative',
              }}
            >
              {fmtDuration(short.durationSeconds) && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    background: 'rgba(0,0,0,0.7)',
                    color: 'var(--color-text-on-brand, #fff)',
                    fontSize: '11px',
                    padding: 'var(--space-0-5, 2px) var(--space-1-5, 6px)',
                    borderRadius: 4,
                  }}
                >
                  {fmtDuration(short.durationSeconds)}
                </span>
              )}
            </div>
            <div style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.3 }}>{short.title}</div>
          </Link>
        ))}
      </ContentRail>
    </SectionShell>
  );
}
